// client/src/pages/secure/ClientDetailPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import ActivityTimeline from '../../components/secure/ActivityTimeline';
import HotLeadPanel from '../../components/secure/HotLeadPanel';
import DocumentManager from '../../components/secure/DocumentManager';
import SiteSearchPanel from '../../components/secure/SiteSearchPanel';
import ClientCmaPanel from '../../components/secure/ClientCmaPanel';
import FormsPanel from '../../components/secure/FormsPanel';
import ClientChecklistPanel from '../../components/secure/ClientChecklistPanel';
import { getCurrentUser } from '../../hooks/useCurrentUser';
import DataList from '../../components/shared/DataList';

const PIPELINE_STAGES = ['Lead', 'Active Prospect', 'Under Contract', 'Closed Client', 'Past Client', 'Lost'];
const CLIENT_DOCUMENT_TYPES = ['Buyer Agency Agreement', 'Pre-Approval Letter', 'Photo ID', 'Correspondence', 'Other'];

// --- Edit Client Modal Component ---
const EditClientModal = ({ isOpen, onClose, onClientUpdated, client }) => {
    const [formData, setFormData] = useState({
        first_name: '', last_name: '', email: '', phone_number: '', client_type: '', pipeline_stage: '',
        street_address: '', city: '', state: '', zip_code: '',
        lead_source: '', tags: '', email_opt_out: false, sms_opt_out: false, do_not_call: false,
        assigned_agent_id: '',
        budget_min: '', budget_max: '', desired_areas: '', min_bedrooms: '', preferred_property_type: '',
        secondary_first_name: '', secondary_last_name: '', secondary_email: '', secondary_phone: ''
    });
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [agents, setAgents] = useState([]);
    const apiClient = useApiClient();

    // The assigned agent decides who can still see a client once the record is
    // restricted, so it belongs on this form rather than in a database update.
    useEffect(() => {
        apiClient.get('/api/agents')
            .then((rows) => setAgents((rows || []).filter((a) => a.status === 'Active')))
            .catch(() => setAgents([]));
    }, [apiClient]);

    useEffect(() => {
        if (client) {
            setFormData({
                first_name: client.first_name,
                last_name: client.last_name,
                email: client.email || '',
                phone_number: client.phone_number || '',
                client_type: client.client_type,
                pipeline_stage: client.pipeline_stage || 'Lead',
                street_address: client.street_address || '',
                city: client.city || '',
                state: client.state || '',
                zip_code: client.zip_code || '',
                lead_source: client.lead_source || '',
                tags: client.tags || '',
                email_opt_out: !!client.email_opt_out,
                sms_opt_out: !!client.sms_opt_out,
                do_not_call: !!client.do_not_call,
                budget_min: client.budget_min ?? '',
                budget_max: client.budget_max ?? '',
                desired_areas: client.desired_areas || '',
                min_bedrooms: client.min_bedrooms ?? '',
                preferred_property_type: client.preferred_property_type || '',
                assigned_agent_id: client.assigned_agent_id || '',
                secondary_first_name: client.secondary_first_name || '',
                secondary_last_name: client.secondary_last_name || '',
                secondary_email: client.secondary_email || '',
                secondary_phone: client.secondary_phone || ''
            });
        }
    }, [client]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');
        try {
            const updatedClient = await apiClient.put(`/api/clients/${client.client_id}`, formData);
            onClientUpdated(updatedClient);
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to update client.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4 overflow-y-auto py-8">
            <div className="bg-white p-4 sm:p-8 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto w-full max-w-2xl">
                <h2 className="text-2xl font-bold text-slate-800 mb-6">Edit Client Details</h2>
                <form onSubmit={handleSubmit} className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
                    <fieldset className="space-y-4">
                        <legend className="text-sm font-semibold text-slate-500 uppercase mb-2">Contact</legend>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <input name="first_name" value={formData.first_name} onChange={handleChange} placeholder="First Name" required className="p-2 border rounded" />
                            <input name="last_name" value={formData.last_name} onChange={handleChange} placeholder="Last Name" required className="p-2 border rounded" />
                            <input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="Email" className="p-2 border rounded" />
                            <input name="phone_number" value={formData.phone_number} onChange={handleChange} placeholder="Phone" className="p-2 border rounded" />
                            <select name="client_type" value={formData.client_type} onChange={handleChange} className="p-2 border rounded">
                                <option>Buyer</option><option>Seller</option><option>Tenant</option><option>Landlord</option>
                            </select>
                            <select name="pipeline_stage" value={formData.pipeline_stage} onChange={handleChange} className="p-2 border rounded">
                                {PIPELINE_STAGES.map(stage => <option key={stage} value={stage}>{stage}</option>)}
                            </select>
                            <input name="street_address" value={formData.street_address} onChange={handleChange} placeholder="Street Address" className="p-2 border rounded md:col-span-2" />
                            <input name="city" value={formData.city} onChange={handleChange} placeholder="City" className="p-2 border rounded" />
                            <input name="state" value={formData.state} onChange={handleChange} placeholder="State" className="p-2 border rounded" />
                            <input name="zip_code" value={formData.zip_code} onChange={handleChange} placeholder="Zip Code" className="p-2 border rounded" />
                            <input name="lead_source" value={formData.lead_source} onChange={handleChange} placeholder="Lead Source (e.g. Referral, Zillow, Sign Call)" className="p-2 border rounded" />
                            <input name="tags" value={formData.tags} onChange={handleChange} placeholder="Tags (comma-separated)" className="p-2 border rounded" />
                            <select name="assigned_agent_id" value={formData.assigned_agent_id} onChange={handleChange} className="p-2 border rounded md:col-span-2">
                                <option value="">Assigned agent — unassigned</option>
                                {agents.map((a) => (
                                    <option key={a.agent_id} value={a.agent_id}>
                                        {a.first_name} {a.last_name}{a.license_number ? ` · ${a.license_number}` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </fieldset>

                    <fieldset className="space-y-4 border-t pt-4">
                        <legend className="text-sm font-semibold text-slate-500 uppercase mb-2">Co-Buyer / Spouse</legend>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <input name="secondary_first_name" value={formData.secondary_first_name} onChange={handleChange} placeholder="First Name" className="p-2 border rounded" />
                            <input name="secondary_last_name" value={formData.secondary_last_name} onChange={handleChange} placeholder="Last Name" className="p-2 border rounded" />
                            <input name="secondary_email" type="email" value={formData.secondary_email} onChange={handleChange} placeholder="Email" className="p-2 border rounded" />
                            <input name="secondary_phone" value={formData.secondary_phone} onChange={handleChange} placeholder="Phone" className="p-2 border rounded" />
                        </div>
                    </fieldset>

                    <fieldset className="space-y-4 border-t pt-4">
                        <legend className="text-sm font-semibold text-slate-500 uppercase mb-2">Buyer / Tenant Requirements</legend>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <input name="budget_min" type="number" value={formData.budget_min} onChange={handleChange} placeholder="Budget Min ($)" className="p-2 border rounded" />
                            <input name="budget_max" type="number" value={formData.budget_max} onChange={handleChange} placeholder="Budget Max ($)" className="p-2 border rounded" />
                            <input name="desired_areas" value={formData.desired_areas} onChange={handleChange} placeholder="Desired Areas" className="p-2 border rounded md:col-span-2" />
                            <input name="min_bedrooms" type="number" value={formData.min_bedrooms} onChange={handleChange} placeholder="Min Bedrooms (residential)" className="p-2 border rounded" />
                            <select name="preferred_property_type" value={formData.preferred_property_type} onChange={handleChange} className="p-2 border rounded">
                                <option value="">Preferred Property Type</option>
                                <optgroup label="Residential">
                                    <option>Single Family</option>
                                    <option>Condo</option>
                                    <option>Townhouse</option>
                                    <option>Multi-Family</option>
                                </optgroup>
                                <optgroup label="Commercial">
                                    <option>Office</option>
                                    <option>Retail</option>
                                    <option>Restaurant</option>
                                    <option>Medical Office</option>
                                    <option>Mixed Use</option>
                                    <option>Hospitality</option>
                                    <option>Special Purpose</option>
                                </optgroup>
                                <optgroup label="Industrial">
                                    <option>Warehouse / Distribution</option>
                                    <option>Flex / Light Industrial</option>
                                    <option>Manufacturing</option>
                                    <option>Cold Storage</option>
                                    <option>Self Storage</option>
                                </optgroup>
                                <optgroup label="Land">
                                    <option>Land</option>
                                    <option>Development Site</option>
                                </optgroup>
                            </select>
                        </div>
                    </fieldset>

                    <fieldset className="space-y-2 border-t pt-4">
                        <legend className="text-sm font-semibold text-slate-500 uppercase mb-2">Communication Preferences</legend>
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                            <input type="checkbox" name="email_opt_out" checked={formData.email_opt_out} onChange={handleChange} /> Email opt-out
                        </label>
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                            <input type="checkbox" name="sms_opt_out" checked={formData.sms_opt_out} onChange={handleChange} /> Text/SMS opt-out
                        </label>
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                            <input type="checkbox" name="do_not_call" checked={formData.do_not_call} onChange={handleChange} /> Do not call
                        </label>
                    </fieldset>

                    {error && <p className="text-red-500 text-sm mt-4">{error}</p>}
                    <div className="flex justify-end gap-4 pt-4 border-t">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                        <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-slate-800 text-white rounded disabled:bg-indigo-400">
                            {isSubmitting ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};


// --- Client Portal Access Panel (Office Manager / managing broker only) ---
const PortalAccessPanel = ({ clientId }) => {
    const apiClient = useApiClient();
    const [account, setAccount] = useState(undefined); // undefined = loading, null = no account
    const [error, setError] = useState('');
    const [isBusy, setIsBusy] = useState(false);
    const [tempCredentials, setTempCredentials] = useState(null);

    const fetchAccount = useCallback(() => {
        apiClient.get(`/api/clients/${clientId}/portal-account`)
            .then(setAccount)
            .catch(err => setError(err.message || 'Failed to load portal account status.'));
    }, [clientId, apiClient]);

    useEffect(() => {
        fetchAccount();
    }, [fetchAccount]);

    const handleInvite = async () => {
        setIsBusy(true);
        setError('');
        try {
            const result = await apiClient.post(`/api/clients/${clientId}/portal-invite`, {});
            setTempCredentials(result);
            fetchAccount();
        } catch (err) {
            setError(err.message || 'Failed to create portal invite.');
        } finally {
            setIsBusy(false);
        }
    };

    if (account === undefined) return null;

    return (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow mt-8">
            <h2 className="text-xl font-bold text-slate-800 mb-4">Client Portal Access</h2>
            {account ? (
                <div className="text-sm text-slate-600 space-y-1 mb-4">
                    <p>Login email: <span className="font-medium text-slate-800">{account.login_email}</span></p>
                    <p>Status: <span className="font-medium text-slate-800">{account.is_active ? 'Active' : 'Inactive'}</span></p>
                    <p>Last login: <span className="font-medium text-slate-800">{account.last_login_at ? new Date(account.last_login_at).toLocaleString() : 'Never'}</span></p>
                </div>
            ) : (
                <p className="text-slate-500 text-sm mb-4">This client does not have portal access yet.</p>
            )}
            <button onClick={handleInvite} disabled={isBusy} className="px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-md hover:bg-slate-900 disabled:bg-indigo-400">
                {isBusy ? 'Working...' : account ? 'Reset Password' : 'Invite to Portal'}
            </button>
            {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
            {tempCredentials && (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md text-sm">
                    <p className="font-semibold text-yellow-800">Share these credentials with the client directly (shown once):</p>
                    <p className="mt-1">Email: <span className="font-mono">{tempCredentials.login_email}</span></p>
                    <p>Temporary password: <span className="font-mono">{tempCredentials.temporary_password}</span></p>
                </div>
            )}
        </div>
    );
};

// --- Main Client Detail Page Component ---
const ClientDetailPage = () => {
    const { id } = useParams();
    const [client, setClient] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [confBusy, setConfBusy] = useState(false);
    const apiClient = useApiClient();
    const currentUser = getCurrentUser();
    const canManagePortalAccess = !!(currentUser?.isManagingBroker || currentUser?.staffRole === 'Office Manager');
    // Only the supervisor may narrow the circle: an agent who could hide a client
    // from their own managing broker is the opposite of sponsorship.
    const isManagingBroker = !!(currentUser?.isManagingBroker || currentUser?.role === 'admin');

    const fetchClientData = useCallback(async () => {
        try {
            const [clientData, transactionsData] = await Promise.all([
                apiClient.get(`/api/clients/${id}`),
                apiClient.get(`/api/clients/${id}/transactions`)
            ]);
            setClient(clientData);
            setTransactions(transactionsData);
        } catch (err) {
            setError(err.message || 'Failed to fetch client data.');
        } finally {
            setIsLoading(false);
        }
    }, [id, apiClient]);

    useEffect(() => {
        fetchClientData();
    }, [fetchClientData]);

    const toggleConfidential = async (on) => {
        setConfBusy(true);
        try {
            await apiClient.put(`/api/clients/${id}/confidential`, { is_confidential: on });
            await fetchClientData();
        } catch (err) {
            alert(err.message || 'Could not change the restriction.');
        } finally { setConfBusy(false); }
    };

    const handleClientUpdated = (updatedClient) => {
        setClient(updatedClient);
    };

    if (isLoading) return <div className="p-8">Loading client details...</div>;
    if (error) return <div className="p-8 text-red-500">{error}</div>;
    if (!client) return <div className="p-8">Client not found.</div>;

    return (
        <>
            <EditClientModal 
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                onClientUpdated={handleClientUpdated}
                client={client}
            />
            <div>
                <div className="mb-6">
                    <Link to="/dashboard/clients" className="text-indigo-600 hover:text-indigo-900">&larr; Back to Client List</Link>
                </div>

                <div className="bg-white p-4 sm:p-8 rounded-lg shadow mb-8">
                    <div className="flex flex-wrap gap-3 justify-between items-center">
                        <h1 className="text-3xl font-bold text-slate-800">{client.first_name} {client.last_name}</h1>
                        <button onClick={() => setIsEditModalOpen(true)} className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-100 rounded-md hover:bg-indigo-200">
                            Edit Client
                        </button>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <p className="text-lg text-slate-600">{client.client_type}</p>
                        <span className="px-2 py-0.5 text-xs font-semibold bg-indigo-100 text-indigo-800 rounded-full">{client.pipeline_stage}</span>
                        {!!client.is_confidential && (
                            <span className="px-2 py-0.5 text-xs font-semibold bg-rose-100 text-rose-800 rounded-full"
                                  title="Visible to the assigned agent, agents on a transaction with this client, and the managing broker.">
                                Confidential
                            </span>
                        )}
                    </div>
                    {isManagingBroker && (
                        <div className="mt-3 rounded border border-slate-200 bg-slate-50 px-3 py-2">
                            <label className="flex items-center gap-2 text-sm text-slate-700">
                                <input type="checkbox" checked={!!client.is_confidential} disabled={confBusy}
                                       onChange={(e) => toggleConfidential(e.target.checked)} />
                                <span className="font-semibold">Restrict this client record</span>
                            </label>
                            <p className="mt-1 text-xs text-slate-500">
                                {client.is_confidential
                                    ? 'Restricted. The assigned agent, any agent on a transaction with this client, and you '
                                      + 'can see it. Team leads cannot. A Division examiner still can — that access is statutory.'
                                    : 'Normal scoping: the assigned agent, agents on their deals, team leads of those agents, and you.'}
                                {client.confidential_note ? ` · ${client.confidential_note}` : ''}
                            </p>
                        </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4 border-t pt-4">
                        <div><p className="text-sm font-medium text-slate-500">Email</p><p className="text-lg text-slate-900">{client.email || 'N/A'}</p></div>
                        <div><p className="text-sm font-medium text-slate-500">Phone</p><p className="text-lg text-slate-900">{client.phone_number || 'N/A'}</p></div>
                        <div className="md:col-span-2"><p className="text-sm font-medium text-slate-500">Address</p><p className="text-lg text-slate-900">{client.street_address}, {client.city}, {client.state} {client.zip_code}</p></div>
                        <div><p className="text-sm font-medium text-slate-500">Lead Source</p><p className="text-lg text-slate-900">{client.lead_source || 'N/A'}</p></div>
                        <div><p className="text-sm font-medium text-slate-500">Tags</p><p className="text-lg text-slate-900">{client.tags || 'N/A'}</p></div>
                        {(client.secondary_first_name || client.secondary_last_name) && (
                            <div className="md:col-span-2">
                                <p className="text-sm font-medium text-slate-500">Co-Buyer / Spouse</p>
                                <p className="text-lg text-slate-900">{client.secondary_first_name} {client.secondary_last_name} {client.secondary_email && `· ${client.secondary_email}`} {client.secondary_phone && `· ${client.secondary_phone}`}</p>
                            </div>
                        )}
                        {(client.budget_min || client.budget_max || client.desired_areas) && (
                            <div className="md:col-span-2">
                                <p className="text-sm font-medium text-slate-500">Buyer Preferences</p>
                                <p className="text-lg text-slate-900">
                                    {client.budget_min && `$${Number(client.budget_min).toLocaleString()}`}
                                    {client.budget_max && ` - $${Number(client.budget_max).toLocaleString()}`}
                                    {client.desired_areas && ` · ${client.desired_areas}`}
                                    {client.min_bedrooms && ` · ${client.min_bedrooms}+ bed`}
                                    {client.preferred_property_type && ` · ${client.preferred_property_type}`}
                                </p>
                            </div>
                        )}
                        {(!!client.email_opt_out || !!client.sms_opt_out || !!client.do_not_call) && (
                            <div className="md:col-span-2 flex gap-2">
                                {!!client.email_opt_out && <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-800 rounded-full">Email opt-out</span>}
                                {!!client.sms_opt_out && <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-800 rounded-full">SMS opt-out</span>}
                                {!!client.do_not_call && <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-800 rounded-full">Do not call</span>}
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-white p-4 sm:p-8 rounded-lg shadow">
                    <h2 className="text-2xl font-bold text-slate-800 mb-6">Transaction History</h2>
                    <div className="overflow-x-auto">
                        <div className="p-4"><DataList rows={transactions} rowKey="transaction_id" csvName="client-transactions" defaultSort={{ key: 'actual_close_date', dir: 'desc' }} empty="No transactions found for this client."
                            filters={[{ key: 'status', label: 'Status' }, { key: 'client_role', label: 'Role' }]}
                            columns={[
                                { key: 'street_address', label: 'Property', render: (t) => <span className="font-medium text-slate-900">{t.street_address}</span> },
                                { key: 'client_role', label: "Client's Role" },
                                { key: 'actual_close_date', label: 'Close Date', type: 'date', render: (t) => (t.actual_close_date ? new Date(t.actual_close_date).toLocaleDateString() : 'N/A') },
                                { key: 'status', label: 'Status' },
                            ]} /></div>
                    </div>
                </div>

                <div className="mt-8 bg-white p-4 sm:p-8 rounded-lg shadow">
                    <SiteSearchPanel clientId={id} />
                </div>

                <div className="mt-8">
                    <ClientCmaPanel clientId={id} />
                </div>

                {/* Above Forms on purpose: the checklist asks whether the firm may act
                    at all, which is the question that comes before which document. */}
                <div className="mt-8">
                    <ClientChecklistPanel clientId={id} transactions={transactions} />
                </div>

                <div className="mt-8">
                    <FormsPanel clientId={id} clientName={`${client.first_name || ''} ${client.last_name || ''}`.trim()} />
                </div>

                <div className="mt-8">
                    <DocumentManager relatedId={id} relatedType="client" documentTypes={CLIENT_DOCUMENT_TYPES} />
                </div>

                {canManagePortalAccess && <PortalAccessPanel clientId={id} />}

                <div className="mt-8"><HotLeadPanel clientId={id} /></div>

                <ActivityTimeline clientId={id} />
            </div>
        </>
    );
};

export default ClientDetailPage;
